/**
 * Admin force-URL helpers.
 *
 * When `VITE_ADMIN_FORCE_URL` is set, admin users are sent to the same path on
 * that origin (e.g. a preview build we want internal users to test). Leaving
 * the variable empty disables the redirect entirely.
 *
 * `VITE_ADMIN_FORCE_URL` is the target origin, e.g. `https://v2.app.bonnie.ai`.
 *
 * Opening any page with `?classic=1` opts this browser out for 24 hours;
 * `?classic=0` ends the opt-out early.
 */

export const ADMIN_FORCE_CLASSIC_PARAM = 'classic'
export const ADMIN_FORCE_CLASSIC_STORAGE_KEY = 'admin_force_classic_until'
export const ADMIN_FORCE_CLASSIC_TTL_MS = 24 * 60 * 60 * 1000

interface ForceLocation {
  origin: string
  pathname: string
  search: string
  hash: string
}

interface ResolveAdminForceUrlOptions {
  forceUrl: string | undefined
  role: string | null | undefined
  location: ForceLocation
  now?: number
  storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>
}

/** Apply `?classic=1|0` to storage and report whether the opt-out is active. */
function isClassicOptOutActive(
  params: URLSearchParams,
  now: number,
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>
): boolean {
  const classic = params.get(ADMIN_FORCE_CLASSIC_PARAM)
  if (classic === '1') {
    storage.setItem(ADMIN_FORCE_CLASSIC_STORAGE_KEY, String(now + ADMIN_FORCE_CLASSIC_TTL_MS))
    return true
  }
  if (classic === '0') {
    storage.removeItem(ADMIN_FORCE_CLASSIC_STORAGE_KEY)
    return false
  }

  const until = Number(storage.getItem(ADMIN_FORCE_CLASSIC_STORAGE_KEY))
  if (until > now) return true
  storage.removeItem(ADMIN_FORCE_CLASSIC_STORAGE_KEY)
  return false
}

/** URL an admin should be redirected to; `null` when no redirect applies. */
export function resolveAdminForceUrl({
  forceUrl,
  role,
  location,
  now = Date.now(),
  storage = window.localStorage,
}: ResolveAdminForceUrlOptions): string | null {
  if (!forceUrl || role !== 'admin') return null

  let target: URL
  try {
    target = new URL(forceUrl)
  } catch {
    return null
  }
  // Never redirect a build to itself (the forced build may carry the same env).
  if (target.origin === location.origin) return null

  const params = new URLSearchParams(location.search)
  if (isClassicOptOutActive(params, now, storage)) return null

  params.delete(ADMIN_FORCE_CLASSIC_PARAM)
  const search = params.toString()
  return `${target.origin}${location.pathname}${search ? `?${search}` : ''}${location.hash}`
}
